<template>
  <main>
    <div class="game-container">
    <div class="button-container">
      <p class="button-instruction">
        {{ gameFinished ? 'Click the button to play again!' : 'Click as fast as you can!' }}
      </p>
      <button 
        @click="handleClick" 
        class="counter-button" 
        :disabled="(gameFinished && !resultSaved) || cooldown"
      >
        {{ gameFinished ? 'Again?' : counter }}
      </button>
      <p class="timer-text">
        Elapsed {{ elapsedTime }}s
      </p>
    </div>

    <div class="results-list">
      <h3>Highscore</h3>
      <table>
        <thead>
          <tr>
            <th>Attempt</th>
            <th>Clicks</th>
            <th>CPS</th>
            <th>Best</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="savedResults.length === 0">
            <td colspan="4" class="no-results">No attempts yet</td>
          </tr>
          <tr v-for="(result, index) in savedResults" :key="index">
            <td>{{ index + 1 }}</td>
            <td>{{ result }}</td>
            <td>{{ (result / gameDuration).toFixed(1) }}</td>
            <td>
              <span v-if="isLatestHighestScore(index)" class="star">⭐</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ClickingGame',
  data() {
    return {
      counter: 0,
      timerStarted: false,
      startTime: null,
      elapsedTime: 0,
      timerInterval: null,
      gameFinished: false,
      savedResults: [],
      resultSaved: false,
      gameDuration: 5,
      cooldown: false
    }
  },
  methods: {
    handleClick() {
      if (this.gameFinished) {
        if (!this.cooldown) {
          this.resetGame();
          this.startTimer();
          this.incrementCounter();
        }
        return;
      }
      if (!this.timerStarted) {
        this.startTimer();
      }
      this.incrementCounter();
    },
    startTimer() {
      this.timerStarted = true;
      this.startTime = Date.now();
      this.timerInterval = setInterval(() => {
        this.elapsedTime = ((Date.now() - this.startTime) / 1000).toFixed(1);
        if (this.elapsedTime >= this.gameDuration) {
          this.endGame();
        }
      }, 100);
    },
    endGame() {
      clearInterval(this.timerInterval);
      this.gameFinished = true;
      this.elapsedTime = this.gameDuration;
      this.savedResults.push(this.counter);
      this.resultSaved = true;
      this.cooldown = true;
      setTimeout(() => {
        this.cooldown = false;
      }, 1000);
    },
    incrementCounter() {
      if (!this.gameFinished) {
        this.counter++;
      }
    },
    resetGame() {
      this.counter = 0;
      this.timerStarted = false;
      this.startTime = null;
      this.elapsedTime = 0;
      this.gameFinished = false;
      this.resultSaved = false;
      this.cooldown = false;
    },
    isLatestHighestScore(index) {
      const highestScore = Math.max(...this.savedResults);
      const lastHighestIndex = this.savedResults.lastIndexOf(highestScore);
      return index === lastHighestIndex;
    }
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
}
</script>

<style>
main {
  padding: 20px;
}

.game-container {
  display: flex;
  gap: 40px;
  align-items: flex-start;
}

.button-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
}

.results-list {
  flex: 1;
  margin-top: 20px;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.results-list h3 {
  color: #4CAF50;
  margin-bottom: 15px;
  text-align: center;
}

.results-list table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
}

.results-list th,
.results-list td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.results-list th {
  background-color: #4CAF50;
  color: white;
  font-weight: 500;
}

.results-list tr:hover {
  background-color: #f5f5f5;
}

.results-list tr:last-child td {
  border-bottom: none;
}

.star {
  font-size: 1.2rem;
}

@media (max-width: 768px) {
  .game-container {
    flex-direction: column;
    align-items: center;
  }

  .button-container {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }

  .results-list {
    width: 100%;
  }

  .results-list th,
  .results-list td {
    padding: 8px;
    font-size: 0.9rem;
  }

  .counter-button {
    margin: 0 auto;
  }
}

.counter-button {
  padding: 20px;
  width: 150px;
  height: 150px;
  font-size: 2rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.counter-button:hover {
  background-color: #c82333;
}

.counter-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.button-instruction {
  text-align: center;
  font-weight: bold;
  color: #4CAF50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.timer-text {
  margin-top: 15px;
  font-size: 2rem;
  font-weight: bold;
  color: #4CAF50;
}


.no-results {
  color: #666;
  font-style: italic;
  padding: 20px !important;
}
</style> 